import BenchlingConfig from 'BenchlingConfig';
import {AgentType, ChatType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {TargetItemInfo} from 'llm/chat-library/getTargetItem';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {assertNever} from 'util/Types';

// Single source of truth for UI order: toolbar agent types (left-to-right).
export const ORDERED_AGENT_TYPES: Array<AgentType> = [
  AgentType.ASK,
  AgentType.DEEP_RESEARCH,
  AgentType.DATA_CAPTURE,
  AgentType.ANALYSIS,
  AgentType.LEGACY_DATA_IMPORT,
];

export function isAISidepanelEnabled(): boolean {
  return getChatTypesForNewAgentChat().length > 0;
}

/**
 * Ordered list of ChatTypes that can be selected when sending a chat message
 *
 * Only returns unified-or-soon-to-be-unified chat options.
 */
export function getChatTypesForNewAgentChat(): Array<ChatType> {
  const allChatTypes = BenchlingConfig.LLM_ENABLE_AGENT_SWITCH
    ? [ChatType.UNIFIED]
    : [ChatType.DEEP_RESEARCH, ChatType.DATA_CAPTURE, ChatType.ANALYSIS];
  return allChatTypes.filter((chatType) => getAvailableAgentTypes([chatType]).length > 0);
}

/**
 * Parse the value out of a URL like/chats/new?agent=<agentTypeQSValue> (see: AIRoutes.ts).
 *
 * NOTE: These URLs may be put in marketing emails, so we should avoid changing the string values
 * when possible, i.e. maintain backwards compatibility. It is acceptable to have multiple querystring
 * values map to the same AgentType.
 */
export function getAgentTypeFromQueryStringValue(agentTypeQSValue: string | null): AgentType | null {
  switch (agentTypeQSValue) {
    case 'ask':
      return AgentType.ASK;
    case 'deep-research':
      return AgentType.DEEP_RESEARCH;
    case 'compose':
      return AgentType.DATA_CAPTURE;
    case 'analysis':
      return AgentType.ANALYSIS;
    default:
      // Non-exhaustive: we will add more agent types here if requested.
      return null;
  }
}

/**
 * Note: this is only used for internal feedback submission.
 */
export function getChatTypeText(chatType: ChatType): string {
  switch (chatType) {
    case ChatType.UNIFIED:
      return 'Chat';
    case ChatType.DEEP_RESEARCH:
      return 'deep research';
    case ChatType.DATA_CAPTURE:
      return 'Compose';
    case ChatType.LEGACY_DATA_IMPORT:
      return 'Legacy Data Import';
    case ChatType.ANALYSIS:
      return 'Analysis';
    default:
      throw new Error(`Unknown chat type: ${assertNever(chatType)}`);
  }
}

export function doesAgentTypePreferExpandedSidepanel(agentType: AgentType): boolean {
  switch (agentType) {
    case AgentType.DATA_CAPTURE:
      return true;
    case AgentType.LEGACY_DATA_IMPORT:
      return true;
    case AgentType.ASK:
      return false;
    case AgentType.DEEP_RESEARCH:
      return false;
    case AgentType.ANALYSIS:
      return false;
    default:
      assertNever(agentType);
      return false;
  }
}

/**
 * Return the chat type to use when creating a new chat for the given agent.
 *
 * When agent switching is enabled, DEEP_RESEARCH, DATA_CAPTURE, ANALYSIS are remapped
 * to UNIFIED so that new chats support agent switching. Other chat types
 * (e.g. LEGACY_DATA_IMPORT) keep their own island.
 */
export function getChatTypeForNewAgentChat(agentType: AgentType): ChatType {
  switch (agentType) {
    case AgentType.DEEP_RESEARCH:
      return BenchlingConfig.LLM_ENABLE_AGENT_SWITCH ? ChatType.UNIFIED : ChatType.DEEP_RESEARCH;
    case AgentType.ASK:
      return BenchlingConfig.LLM_ENABLE_AGENT_SWITCH ? ChatType.UNIFIED : ChatType.DEEP_RESEARCH;
    case AgentType.DATA_CAPTURE:
      return BenchlingConfig.LLM_ENABLE_AGENT_SWITCH ? ChatType.UNIFIED : ChatType.DATA_CAPTURE;
    case AgentType.ANALYSIS:
      return BenchlingConfig.LLM_ENABLE_AGENT_SWITCH ? ChatType.UNIFIED : ChatType.ANALYSIS;
    case AgentType.LEGACY_DATA_IMPORT:
      return ChatType.LEGACY_DATA_IMPORT;
    default:
      throw new Error(`Unknown agent type: ${assertNever(agentType)}`);
  }
}

export function getAgentTypeIcon(agentType: AgentType): BenchlingIconName | null {
  switch (agentType) {
    case AgentType.DEEP_RESEARCH:
      return 'file-magnifying-glass';
    case AgentType.DATA_CAPTURE:
      return 'pen';
    case AgentType.LEGACY_DATA_IMPORT:
      return 'import';
    case AgentType.ASK:
      return 'comments';
    case AgentType.ANALYSIS:
      return 'lightbulb';
    default:
      assertNever(agentType);
      return null;
  }
}

export function getAgentTypeText(agentType: AgentType): string {
  switch (agentType) {
    case AgentType.DEEP_RESEARCH:
      return 'Deep Research';
    case AgentType.ASK:
      return 'Ask';
    case AgentType.DATA_CAPTURE:
      return 'Compose';
    case AgentType.LEGACY_DATA_IMPORT:
      return 'Data Import';
    case AgentType.ANALYSIS:
      return 'Analysis';
    default:
      assertNever(agentType);
      throw new Error(`Unknown agent type: ${assertNever(agentType)}`);
  }
}

export function isAgentTypeEnabled(agentType: AgentType): boolean {
  switch (agentType) {
    case AgentType.DEEP_RESEARCH:
      return BenchlingConfig.LLM_ENABLE_AI_DEEP_RESEARCH;
    case AgentType.DATA_CAPTURE:
      return BenchlingConfig.LLM_ENABLE_AI_DATA_CAPTURE_ASSISTANT;
    case AgentType.LEGACY_DATA_IMPORT:
      return BenchlingConfig.LLM_ENABLE_AI_DATA_CAPTURE_ASSISTANT;
    case AgentType.ASK:
      return BenchlingConfig.LLM_ENABLE_AI_DEEP_RESEARCH;
    case AgentType.ANALYSIS:
      return BenchlingConfig.LLM_ENABLE_AI_ANALYSIS_AGENT;
    default:
      assertNever(agentType);
      return false;
  }
}

export function getAvailableAgentTypes(chatTypes: Array<ChatType>): Array<AgentType> {
  if (BenchlingConfig.LLM_ENABLE_AGENT_SWITCH) {
    // Re-map legacy chat types to UNIFIED in case we are dealing with an old chat.
    chatTypes = [
      ...new Set(
        chatTypes.map((chatType) =>
          chatType === ChatType.DEEP_RESEARCH ||
          chatType === ChatType.DATA_CAPTURE ||
          chatType === ChatType.ANALYSIS
            ? ChatType.UNIFIED
            : chatType
        )
      ),
    ];
  }
  return ORDERED_AGENT_TYPES.filter(
    (agentType) => isAgentTypeEnabled(agentType) && chatTypes.includes(getChatTypeForNewAgentChat(agentType))
  );
}

/**
 * Get the default agent type for new chats.
 *
 * If initialAgentType is provided and available, it will be used.
 * Otherwise, defaults to Ask if available, or the first available agent type.
 */
export function getDefaultAgentType(initialAgentType?: AgentType | null): AgentType | null {
  const availableAgentTypes = getAvailableAgentTypes(getChatTypesForNewAgentChat());
  if (initialAgentType && availableAgentTypes.includes(initialAgentType)) {
    return initialAgentType;
  }
  // Default to Ask for new chats
  return availableAgentTypes.includes(AgentType.ASK) ? AgentType.ASK : (availableAgentTypes[0] ?? null);
}

export function getAgentTypePlaceholderText(
  agentType: AgentType,
  targetItem: TargetItemInfo | null = null
): string {
  switch (agentType) {
    case AgentType.DEEP_RESEARCH:
      return 'Write detailed reports based on your data';
    case AgentType.ASK:
      return 'Ask questions about your data';
    case AgentType.DATA_CAPTURE:
      if (targetItem != null) {
        return 'Capture data, keep notes, or ask questions';
      } else {
        return 'Draft an entry or template';
      }
    case AgentType.LEGACY_DATA_IMPORT:
      return 'Transcribe a file into a Benchling document';
    case AgentType.ANALYSIS:
      return 'Search and explore your analyses and datasets';
    default:
      assertNever(agentType);
      return '';
  }
}

/**
 * doesAgentTypeSupportSources must also return true to allow public data to be used.
 */
export function doesAgentTypeSupportPublicData(agentType: AgentType): boolean {
  switch (agentType) {
    case AgentType.DEEP_RESEARCH:
      return BenchlingConfig.LLM_ENABLE_AI_DEEP_RESEARCH_PUBLIC_DATA;
    case AgentType.ASK:
      return false;
    case AgentType.DATA_CAPTURE:
      return false;
    case AgentType.LEGACY_DATA_IMPORT:
      return false;
    case AgentType.ANALYSIS:
      return false;
    default:
      assertNever(agentType);
      return false;
  }
}

/**
 * Indicate if the agent backend supports setting CreateDisplayMessageChatEvent['sources'].
 */
export function doesAgentTypeSupportSources(agentType: AgentType): boolean {
  switch (agentType) {
    case AgentType.DEEP_RESEARCH:
      return true;
    case AgentType.DATA_CAPTURE:
      return false;
    case AgentType.ASK:
      return false;
    case AgentType.LEGACY_DATA_IMPORT:
      return false;
    case AgentType.ANALYSIS:
      return false;
    default:
      assertNever(agentType);
      return false;
  }
}

/**
 * Indicate if the agent type supports workspace context (active workspace item as context).
 */
export function doesAgentTypeSupportWorkspaceContext(agentType: AgentType): boolean {
  switch (agentType) {
    case AgentType.ASK:
      return true;
    case AgentType.DEEP_RESEARCH:
      return false;
    case AgentType.DATA_CAPTURE:
      return false;
    case AgentType.LEGACY_DATA_IMPORT:
      return false;
    case AgentType.ANALYSIS:
      return false;
    default:
      assertNever(agentType);
      return false;
  }
}

/**
 * Indicate if the chat type should show progress UI (tool calls, activity tracking).
 *
 * TODO: This should be unconditionally true.
 */
export function doesChatTypeShowProgress(chatType: ChatType): boolean {
  switch (chatType) {
    case ChatType.UNIFIED:
    case ChatType.DEEP_RESEARCH:
      return true;
    case ChatType.DATA_CAPTURE:
      return true;
    case ChatType.LEGACY_DATA_IMPORT:
      return true;
    case ChatType.ANALYSIS:
      return true;
    default:
      assertNever(chatType);
      return false;
  }
}
